import { GameLevel, ParticipationStatus, Game } from '@/types/game'

// Game level semantic tokens using design system and enums
export const NIVEL_COLORS = {
  [GameLevel.BEGINNER]: {
    background: 'bg-game-level-beginner-bg',
    text: 'text-game-level-beginner-text',
    border: 'border-game-level-beginner-border',
  },
  [GameLevel.INTERMEDIATE]: {
    background: 'bg-game-level-intermediate-bg',
    text: 'text-game-level-intermediate-text',
    border: 'border-game-level-intermediate-border',
  },
  [GameLevel.ADVANCED]: {
    background: 'bg-game-level-advanced-bg',
    text: 'text-game-level-advanced-text',
    border: 'border-game-level-advanced-border',
  },
  [GameLevel.OPEN]: {
    background: 'bg-game-level-open-bg',
    text: 'text-game-level-open-text',
    border: 'border-game-level-open-border',
  }
} as const

// Vacancy status tokens using design system
export const VAGAS_COLORS = {
  disponivel: {
    background: 'bg-game-vacancy-available-bg',
    text: 'text-game-vacancy-available-text'
  },
  limitado: {
    background: 'bg-game-vacancy-limited-bg',
    text: 'text-game-vacancy-limited-text'
  },
  lotado: {
    background: 'bg-game-vacancy-full-bg',
    text: 'text-game-vacancy-full-text'
  }
} as const

// Função para obter cores do nível usando enum
export function getNivelColors(nivel: GameLevel) {
  return NIVEL_COLORS[nivel] || NIVEL_COLORS[GameLevel.OPEN]
}

// Função para obter cores das vagas
export function getVagasColors(vagasOcupadas: number, vagasTotal: number) {
  const percentOccupied = (vagasOcupadas / vagasTotal) * 100
  
  if (percentOccupied >= 90) return VAGAS_COLORS.lotado
  if (percentOccupied >= 70) return VAGAS_COLORS.limitado
  return VAGAS_COLORS.disponivel
}

// Função para formatar classes CSS
export function buildColorClasses(colors: Record<string, string>) {
  return Object.values(colors).join(' ')
}

// Função helper para obter informações do status de participação
export function getParticipationStatusInfo(userStatus: ParticipationStatus) {
  switch (userStatus) {
    case ParticipationStatus.CONFIRMED:
      return {
        label: '✅ Você vai',
        className: 'bg-status-success-bg text-status-success-text border-status-success-border'
      }
    case ParticipationStatus.INVITED:
      return {
        label: '📩 Você foi convidado',
        className: 'bg-status-warning-bg text-status-warning-text border-status-warning-border'
      }
    case ParticipationStatus.DECLINED:
      return {
        label: '❌ Você recusou',
        className: 'bg-status-error-bg text-status-error-text border-status-error-border'
      }
    case ParticipationStatus.CANCELED:
      return {
        label: '🚫 Você cancelou',
        className: 'bg-background-tertiary text-text-tertiary border-border-secondary'
      }
    case ParticipationStatus.NOT_PARTICIPANT:
    default:
      return null // Don't show any status for non-participants
  }
}

// Função helper para verificar se o jogo está lotado
export function isGameFull(game: Game): boolean {
  return game.occupiedSlots >= game.totalSlots
}

// Função helper para verificar se participação é possível
export function canParticipate(game: Game, userStatus?: ParticipationStatus): boolean {
  // Se o jogo está lotado, só permite se já estiver confirmado
  if (isGameFull(game)) {
    return userStatus === ParticipationStatus.CONFIRMED
  }
  return true
}

// Função helper para verificar se convites são possíveis
export function canInvite(game: Game): boolean {
  return !isGameFull(game)
}

// Função helper para verificar se é possível aprovar participantes
export function canApproveParticipants(game: Game): boolean {
  return !isGameFull(game)
}